import { AuthConfig } from 'angular-oauth2-oidc';
import { Moment } from 'moment';

export interface ICrowdinConfig {
  enable: boolean;
  languageKey?: string;
}

export interface ICompanyLogoConfig {
  url: string;
  name: string;
  extensions: string[];
}

export interface IAppConfiguration {
  authConfig: AuthConfig;
  i18nConfig: {};
  serverApiUrl: string;
  sessionTimeout: number;
  companyLogo: ICompanyLogoConfig;
  globalChangePasswordUri?: string;
  globalChangePasswordUriDocaposte?: string;
  navbarHelpUrl?: string;
  assistenzaUrl?: string;
  accessHistoryGuideUrl?: string;
  accessHistoryRevokeAccessUrl?: string;
  appCompatibilityCheckUrl?: string;
  genericErrorUrl?: string;
  docapostegenericErrorUrl?: string;
  docaposteUri?: string;
  twofaClientHelpUrl?: string;
  crowdin: ICrowdinConfig;
  twoFaEnabled: boolean;
  highContrastEnabled: boolean;
  usabillaEnabled: boolean;
  baseHref: string;
  changeContact: string;
  rememberMe: number;
  pgaUrl: string;
  webmailUrl: string;
  guideQuestionPecPlus: string;
  assistenzaDossier: string;
  appVersionAndroid: string;
  appVersionIOS: string;
  pecConversionExpireDate: Moment;
  pecConversionRememberTimeout: number;
  conversionStrategy1OK: boolean;
  conversionStrategyDocaposte: number;
  cancelledGuideUrl: string;
  PECCustomerArea: string;
  env: string;
  beta: string;
  hrefJavascript: string;
}

const DEFAULTS = {
  rememberMe: 15,
  pecConversionRememberTimeout: 5,
  conversionStrategyDocaposte: 10
};

/**
 * Application configuration model
 */
export class AppConfiguration implements IAppConfiguration {
  authConfig: AuthConfig;
  i18nConfig: {};
  serverApiUrl: string;
  sessionTimeout: number;
  companyLogo: ICompanyLogoConfig;
  globalChangePasswordUri?: string;
  globalChangePasswordUriDocaposte?: string;
  navbarHelpUrl?: string;
  assistenzaUrl?: string;
  accessHistoryGuideUrl?: string;
  accessHistoryRevokeAccessUrl?: string;
  appCompatibilityCheckUrl?: string;
  genericErrorUrl?: string;
  docapostegenericErrorUrl?: string;
  docaposteUri?: string;
  twofaClientHelpUrl?: string;
  crowdin: ICrowdinConfig;
  twoFaEnabled: boolean;
  highContrastEnabled: boolean;
  usabillaEnabled: boolean;
  baseHref: string;
  changeContact: string;
  rememberMe: number;
  pgaUrl: string;
  webmailUrl: string;
  guideQuestionPecPlus: string;
  assistenzaDossier: string;
  appVersionAndroid: string;
  appVersionIOS: string;
  pecConversionExpireDate: Moment;
  pecConversionRememberTimeout: number;
  conversionStrategy1OK: boolean;
  conversionStrategyDocaposte: number;
  cancelledGuideUrl: string;
  PECCustomerArea: string;
  env: string;
  beta: string;
  hrefJavascript: string;

  constructor(baseConfig: IAppConfiguration) {
    this.authConfig = this.getObjectValue(baseConfig.authConfig);
    this.i18nConfig = this.getObjectValue(baseConfig.i18nConfig);
    this.serverApiUrl = baseConfig.serverApiUrl;
    this.sessionTimeout = this.getSessionTimeoutValue(baseConfig.sessionTimeout);
    this.companyLogo = this.getCompanyLogoValue(baseConfig.companyLogo);
    this.globalChangePasswordUri = this.getStringValue(baseConfig.globalChangePasswordUri);
    this.globalChangePasswordUriDocaposte = this.getStringValue(baseConfig.globalChangePasswordUriDocaposte);
    this.navbarHelpUrl = this.getUrlValue(baseConfig.navbarHelpUrl);
    this.assistenzaUrl = this.getUrlValue(baseConfig.assistenzaUrl);
    this.accessHistoryGuideUrl = this.getUrlValue(baseConfig.accessHistoryGuideUrl);
    this.accessHistoryRevokeAccessUrl = this.getUrlValue(baseConfig.accessHistoryRevokeAccessUrl);
    this.appCompatibilityCheckUrl = this.getUrlValue(baseConfig.appCompatibilityCheckUrl);
    this.genericErrorUrl = this.getUrlValue(baseConfig.genericErrorUrl);
    this.docapostegenericErrorUrl = this.getUrlValue(baseConfig.docapostegenericErrorUrl);
    this.docaposteUri = this.getStringValue(baseConfig.docaposteUri);
    this.twofaClientHelpUrl = this.getUrlValue(baseConfig.twofaClientHelpUrl);
    this.crowdin = this.getBaseCrowdinValue(baseConfig.crowdin);
    this.twoFaEnabled = baseConfig.twoFaEnabled;
    this.highContrastEnabled = baseConfig.highContrastEnabled;
    this.usabillaEnabled = baseConfig.usabillaEnabled;
    this.baseHref = this.getStringValue(baseConfig.baseHref);
    this.changeContact = this.getStringValue(baseConfig.changeContact);
    this.rememberMe = this.getNumberValue('rememberMe', baseConfig.rememberMe);
    this.pgaUrl = this.getStringValue(baseConfig.pgaUrl);
    this.webmailUrl = this.getStringValue(baseConfig.webmailUrl);
    this.guideQuestionPecPlus = this.getStringValue(baseConfig.guideQuestionPecPlus);
    this.assistenzaDossier = this.getStringValue(baseConfig.assistenzaDossier);
    this.appVersionAndroid = this.getStringValue(baseConfig.appVersionAndroid);
    this.appVersionIOS = this.getStringValue(baseConfig.appVersionIOS);
    this.pecConversionExpireDate = this.getStringValue(baseConfig.pecConversionExpireDate) as Moment;
    this.pecConversionRememberTimeout = this.getNumberValue('pecConversionRememberTimeout', baseConfig.pecConversionRememberTimeout);
    this.conversionStrategy1OK = !!baseConfig.conversionStrategy1OK;
    this.conversionStrategyDocaposte = this.getNumberValue('conversionStrategyDocaposte', baseConfig.conversionStrategyDocaposte);
    this.cancelledGuideUrl = this.getStringValue(baseConfig.cancelledGuideUrl);
    this.PECCustomerArea = this.getStringValue(baseConfig.PECCustomerArea);
    this.env = this.getStringValue(baseConfig.env);
    this.beta = this.getStringValue(baseConfig.beta);
    this.hrefJavascript = this.getStringValue(baseConfig.hrefJavascript);
  }

  private getObjectValue<T>(value: T) {
    return value || {};
  }

  private getStringValue<T>(value?: T) {
    return value || '';
  }

  private getUrlValue(value?: string) {
    return value || '#';
  }

  private getSessionTimeoutValue(sessionTimeout: number | boolean) {
    return typeof sessionTimeout === 'boolean' ? 0 : sessionTimeout
  }

  private getCompanyLogoValue(companyLogo: ICompanyLogoConfig) {
    return companyLogo || ({ url: '', name: 'main_logo', extensions: ['png'] } as ICompanyLogoConfig)
  }

  private getBaseCrowdinValue(crowdin: ICrowdinConfig) {
    return crowdin || ({ enable: false } as ICrowdinConfig)
  }

  private getNumberValue(key: keyof typeof DEFAULTS, value?: number) {
    return value || DEFAULTS[key];
  }
}
